import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import adminAxios from '../../services/adminAxios';
import { formatDate } from '../../utils/dateHelper';
import '../../assets/css/admin/events.css';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const eventsPerPage = 10;

  const fetchEvents = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      // Fetch all events without pagination first to get complete data
      const response = await adminAxios.get(`/api/admin/events?include_stats=true`);
      
      if (response.data.success) {
        setEvents(response.data.events);
        setError(null);
      } else {
        setError('Failed to load events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and search events
  useEffect(() => {
    let filtered = [...events];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(event => 
        event.event_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.creator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus) {
      filtered = filtered.filter(event => 
        (event.status || 'active').toLowerCase() === filterStatus.toLowerCase()
      );
    }

    setFilteredEvents(filtered);
    
    // Calculate pagination for filtered results
    const pages = Math.ceil(filtered.length / eventsPerPage);
    setTotalPages(pages);
    
    // Reset to first page when filters change
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [events, searchTerm, filterStatus, currentPage]);

  // Get current page events
  const getCurrentPageEvents = () => {
    const startIndex = (currentPage - 1) * eventsPerPage;
    const endIndex = startIndex + eventsPerPage;
    return filteredEvents.slice(startIndex, endIndex);
  };

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const currentPageEvents = getCurrentPageEvents();

  return (
    <AdminLayout>
      <div className="container-fluid px-4">
        <h1 className="mt-4">Events Management</h1>
        <ol className="breadcrumb mb-4">
          <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
          <li className="breadcrumb-item active">Events</li>
        </ol>
        
        <AdminCard 
          title={`All Events (${filteredEvents.length} ${filterStatus ? `${filterStatus} ` : ''}${searchTerm ? 'matching' : 'total'})`}
          headerActions={
            <div className="d-flex gap-2">
              <input 
                type="text"
                className="form-control form-control-sm"
                placeholder="Search events..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ minWidth: '200px' }}
              />
              <select 
                className="form-select form-select-sm"
                value={filterStatus}
                onChange={handleStatusFilterChange}
                style={{ minWidth: '120px' }}
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          }
          noPadding
        >
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div 
              className="alert alert-danger" 
              style={{ 
                backgroundColor: '#dc3545',
                borderColor: '#dc3545',
                color: '#ffffff' 
              }}
            >
              {error}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover table-bordered events-table" style={{backgroundColor: 'var(--dark-bg-secondary)'}}>
                  <thead>
                    <tr style={{backgroundColor: 'var(--dark-bg-tertiary)'}}>
                      <th>Status</th>
                      <th>Event Name</th>
                      <th>Date</th>
                      <th>Creator</th>
                      <th>Check-ins</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageEvents.length > 0 ? currentPageEvents.map(event => (
                      <tr key={event.id}>
                        <td>
                          <span className={`status-indicator ${event.status || 'active'}`}></span>
                          <span className={`status-badge ${event.status || 'active'}`}>
                            {(event.status || 'Active').charAt(0).toUpperCase() + (event.status || 'active').slice(1)}
                          </span>
                        </td>
                        <td className="event-name">{event.event_title}</td>
                        <td>{formatDate(event.event_date)}</td>
                        <td>{event.creator_name || 'Unknown'}</td>
                        <td>
                          <span className="badge bg-primary">
                            {event.checkin_count !== undefined ? event.checkin_count : '—'}
                          </span>
                        </td>
                        <td>
                          <Link to={`/admin/events/${event.id}`} className="btn btn-sm btn-outline-primary">
                            <i className="fa fa-eye me-1"></i> View
                          </Link>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="empty-state-inline">
                            <i className="fa fa-calendar-o me-2"></i>
                            <span>
                              {searchTerm || filterStatus 
                                ? 'No events match your search criteria' 
                                : 'No events found'
                              }
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 px-3 pb-3">
                  <div className="text-muted small">
                    Showing {((currentPage - 1) * eventsPerPage) + 1} to {Math.min(currentPage * eventsPerPage, filteredEvents.length)} of {filteredEvents.length} events
                  </div>
                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default EventsPage;
